import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import useDesktopScope from './useDesktopScope';
import { rendererTransferActionAllowed } from '@/components/settings/portableTransferPresentation';

const EMPTY = Object.freeze({});
const requests = new WeakMap();
const listeners = new Set();
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
function update(bridge, patch) {
  requests.set(bridge, { ...(requests.get(bridge) || EMPTY), ...patch });
  for (const listener of listeners) listener();
}
const ACTIONS = Object.freeze({
  preparePortableTransfer: 'prepare', disablePortableTransfer: 'disable', exportPortablePackage: 'export',
  selectPortablePackage: 'import', confirmPortableImport: 'confirmImport', resumePortableImport: 'resumeImport',
  discardPortableImport: 'discardImport', restorePreviousCache: 'restorePrevious', deletePreviousCache: 'deletePrevious',
  removeFailedPortableImport: 'removeFailedImport', checkPortableTransfer: 'checkAgain', stopPortableTransfer: 'stop',
  checkRendererRequestRecovery: 'checkRequestRecovery',
  retireLegacyRequestWarning: 'retireLegacyWarning',
});
const record = value => value && typeof value === 'object' && !Array.isArray(value);
const RETIREMENT_STATES = new Set(['eligible', 'ineligible', 'removing', 'reconciling', 'complete', 'unavailable']);
export function validTransferStatus(value) {
  if (!record(value) || value.schemaVersion !== 1 || !Number.isSafeInteger(value.revision)
      || value.revision < 0 || typeof value.available !== 'boolean') return false;
  if (value.actions !== undefined && (!record(value.actions)
      || Object.values(value.actions).some(flag => typeof flag !== 'boolean'))) return false;
  if (value.busy !== undefined && typeof value.busy !== 'boolean') return false;
  if (value.operation !== undefined && (!record(value.operation)
      || typeof value.operation.kind !== 'string' || typeof value.operation.phase !== 'string')) return false;
  if (value.confirmedRetirement != null && (!record(value.confirmedRetirement)
      || !RETIREMENT_STATES.has(value.confirmedRetirement.state)
      || (value.confirmedRetirement.reason != null && typeof value.confirmedRetirement.reason !== 'string'))) return false;
  return ['preparation', 'import', 'previousCache', 'recovery', 'transferRecovery'].every(
    key => value[key] === undefined || value[key] === null || record(value[key]),
  );
}

// TCA-26101912: native effects stay with native owners. These claims only prevent
// duplicate renderer dispatch across panel retirement; no payloads/credentials persist.
export default function usePortableTransferOwner() {
  const scope = useDesktopScope();
  const bridge = scope.bridge;
  const owner = useMemo(() => ({ scope, active: false, value: null, phase: 'loading', revision: -1,
    version: 0, read: 0, pending: new Set() }), [scope]);
  const [view, setView] = useState(null);
  const lookup = useCallback(() => requests.get(bridge) || EMPTY, [bridge]);
  const claims = useSyncExternalStore(subscribe, lookup, () => EMPTY);
  const current = useCallback(() => owner.active && scope.current() && scope.transfer, [owner, scope]);
  const publish = useCallback(() => {
    if (current()) setView({ owner, status: owner.value, phase: owner.phase });
  }, [current, owner]);
  const accept = useCallback((value, startedVersion = owner.version) => {
    if (!current() || !validTransferStatus(value) || value.revision < owner.revision
        || (value.revision === owner.revision && startedVersion !== owner.version)) return false;
    owner.value = value; owner.phase = 'ready'; owner.revision = value.revision; owner.version++;
    publish();
    return true;
  }, [current, owner, publish]);
  const refresh = useCallback(async () => {
    if (!current() || typeof bridge?.getPortableTransferStatus !== 'function') return false;
    const read = ++owner.read;
    const version = owner.version;
    const barrier = lookup().uncertain;
    let timer; let retire;
    const deadline = new Promise((_, reject) => {
      retire = () => reject(new Error('Status unavailable'));
      timer = setTimeout(retire, 30000);
    });
    owner.pending.add(retire);
    try {
      const value = await Promise.race([Promise.resolve().then(() => {
        if (!current()) throw new Error('Retired');
        return bridge.getPortableTransferStatus();
      }), deadline]);
      if (!current() || read !== owner.read) return false;
      if (!validTransferStatus(value)) throw new Error('Invalid status');
      const adopted = accept(value, version);
      if (adopted && barrier && lookup().uncertain === barrier) update(bridge, { uncertain: null });
      return adopted;
    } catch {
      if (current() && read === owner.read && version === owner.version) {
        owner.phase = 'unavailable'; publish();
      }
      return false;
    } finally { clearTimeout(timer); owner.pending.delete(retire); }
  }, [current, bridge, owner, lookup, accept, publish]);
  useEffect(() => {
    owner.active = true;
    let unsubscribe;
    if (scope.transfer && typeof bridge?.getPortableTransferStatus === 'function') {
      try {
        unsubscribe = bridge.onPortableTransferStatusChanged?.(value => {
          if (!current()) return;
          if (!validTransferStatus(value)) {
            owner.phase = 'unavailable'; owner.version++; publish();
            return;
          }
          if (accept(value)) {
            // An event observed after uncertainty is fresh native admission evidence,
            // never a claim that the earlier command had no effect.
            if (lookup().uncertain) update(bridge, { uncertain: null });
          }
        });
      } catch { /* Initial read can still establish current facts. */ }
      refresh();
    }
    return () => {
      owner.active = false;
      for (const retire of owner.pending) retire();
      unsubscribe?.();
    };
  }, [owner, scope, bridge, accept, refresh, lookup, current, publish]);

  const run = async (method, settled, started) => {
    if (!current() || typeof bridge?.[method] !== 'function') return false;
    const report = ['savePortableSendReport', 'savePortableReceiveReport'].includes(method);
    const lane = report ? 'report' : method === 'stopPortableTransfer' ? 'stop' : 'ordinary';
    const status = owner.value;
    const held = lookup();
    if (held[lane]) return false;
    if (report) {
      const flag = method === 'savePortableSendReport' ? 'saveSendReport' : 'saveReceiveReport';
      if (!scope.admin || status?.actions?.[flag] === false) return false;
    } else {
      if (!Object.hasOwn(ACTIONS, method) || !rendererTransferActionAllowed(status, ACTIONS[method], scope.admin)) return false;
      if (lane === 'ordinary' && (held.stop || held.uncertain || owner.phase !== 'ready' || status.busy)) return false;
    }
    const token = {};
    const version = owner.version;
    update(bridge, { [lane]: token });
    started?.();
    let result; let failed = false;
    try {
      result = await bridge[method]();
      failed = !report && !validTransferStatus(result?.status);
    } catch { failed = true; }
    try {
      if (!report && failed) update(bridge, { uncertain: token });
      if (current()) {
        if (validTransferStatus(result?.status)) accept(result.status, version);
        settled?.(result, failed);
        if (!report && failed) await refresh();
      }
    } finally {
      if (lookup()[lane] === token) update(bridge, { [lane]: null });
    }
    return true;
  };
  const visible = view?.owner === owner && scope.current() ? view : null;
  return { scope, bridge, status: visible?.status || null, phase: visible?.phase || 'loading',
    sending: !!claims.ordinary, stopping: !!claims.stop, savingReport: !!claims.report,
    uncertain: !!claims.uncertain, run, refresh };
}
